import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { Progress, Icon } from "semantic-ui-react";

const FileUploader = ({
                        files,
                        setFiles,
                        maxFiles,
                        errors,
                        disabled,
                        documentData,
                        isOnTheModal,
                        onFileSelected,
                        extensionOptions, // may be [{keyId, keyValue1, keyValue2, keyDescription}] OR [{key, value, text}] etc.
                      }) => {
  const [uploadProgress, setUploadProgress] = useState(0);

  // ---------- helpers ----------
  const clean = (v) => (v == null ? "" : String(v).trim());
  const lower = (v) => clean(v).toLowerCase();
  const normalizeExt = (v) => {
    const s = lower(v);
    if (!s) return null;
    // handle values like ".pdf", "pdf", "PDF ", " Portable Document Format (PDF) "
    // take last token if there's spaces/parentheses, but keep simple too
    // First, strip trailing ")" and anything before "(" to catch "PDF (.pdf)" patterns
    let token = s;
    // try to find something ending with common ext-looking token
    const match = token.match(/(\.[a-z0-9]+)$/i);
    if (match) return match[1].toLowerCase();
    // fallback: use last word, prefix dot if missing
    const parts = token.split(/[^a-z0-9.]+/).filter(Boolean);
    const last = parts[parts.length - 1];
    if (!last) return null;
    return last.startsWith(".") ? last : `.${last}`;
  };

  // Extract ALL possible extensions from a string like "JPG / JPEG" or "PDF"
  const normalizeExtList = (v) => {
    const s = lower(v);
    if (!s) return [];
    // Split by common delimiters: /, \, |, "or", "and"
    const tokens = s.split(/[\\/|]|(?:\s+(?:or|and)\s+)/).map(t => t.trim()).filter(Boolean);
    const exts = [];
    for (const token of tokens) {
      const normalized = normalizeExt(token);
      if (normalized) exts.push(normalized);
    }
    return exts;
  };

  const getFileExt = (file) => {
    const name = clean(file?.name);
    const idx = name.lastIndexOf(".");
    if (idx === -1) return null;
    return name.substring(idx).toLowerCase();
  };

  // ---------- robust extractors from extensionOptions ----------
  const allowedMimes = useMemo(() => {
    const mimes = new Set();
    (extensionOptions || []).forEach((x) => {
      const raw =
          x?.keyValue2 ?? x?.mime ?? x?.contentType ?? ""; // try several keys
      const m = lower(raw);
      if (m) mimes.add(m);
    });
    return mimes;
  }, [extensionOptions]);

  const allowedExts = useMemo(() => {
    const exts = new Set();
    (extensionOptions || []).forEach((x) => {
      const candidateFields = [
        x?.keyValue1,      // ".pdf" | "pdf" | "JPG / JPEG"
        x?.keyDescription, // "PDF"
        x?.text,           // common in dropdown options
        x?.label,          // common in UI libs
      ];
      candidateFields.forEach(field => {
        const extList = normalizeExtList(field);
        extList.forEach((e) => exts.add(e));
      });
    });
    return exts;
  }, [extensionOptions]);

  // For file chooser hint only (real validation happens below)
  const accept = useMemo(() => {
    const mimeList = Array.from(allowedMimes);
    const extList = Array.from(allowedExts);
    if (mimeList.length) {
      const mapping = {};
      mimeList.forEach((m) => {
        mapping[m] = extList.length ? extList : undefined;
      });
      return mapping;
    }
    if (extList.length) return { "*/*": extList };
    return undefined;
  }, [allowedMimes, allowedExts]);

  // ---------- core add ----------
  const addFiles = (newFile) => {
    if (!newFile) return;

    if (newFile.size === 0) {
      toast.error("File has a size of zero...", {
        autoClose: 5000,
        containerId: isOnTheModal ? "Modal" : "Main",
      });
      return;
    }

    if (!extensionOptions || extensionOptions.length === 0) {
      toast.error("Please select document category first", {
        autoClose: 5000,
        containerId: isOnTheModal ? "Modal" : "Main",
      });
      return;
    }

    // If the category has options but we couldn't derive any allow-lists,
    // show a clearer message (backend mapping issue).
    if (allowedMimes.size === 0 && allowedExts.size === 0) {
      console.warn(
          "[Uploader] extensionOptions present but no MIME/ext could be derived.",
          extensionOptions
      );
      toast.error("No allowed file types configured for this category.", {
        autoClose: 5000,
        containerId: isOnTheModal ? "Modal" : "Main",
      });
      return;
    }

    const fileMime = lower(newFile.type); // may be ""
    const fileExt = getFileExt(newFile);
    const fileName = clean(newFile.name);

    const mimeOk = fileMime && allowedMimes.has(fileMime);
    const extOk = fileExt && allowedExts.has(fileExt);
    const suffixOk =
        !extOk &&
        !!fileName &&
        Array.from(allowedExts).some((ext) =>
            fileName.toLowerCase().endsWith(ext)
        );

    // Debug
    console.log("[Uploader] file:", { name: fileName, type: fileMime, ext: fileExt });
    console.log("[Uploader] Allowed mimes:", Array.from(allowedMimes));
    console.log("[Uploader] Allowed exts:", Array.from(allowedExts));

    if (!mimeOk && !extOk && !suffixOk) {
      toast.error(
          "This file type is not supported for the selected document category",
          { autoClose: 5000, containerId: isOnTheModal ? "Modal" : "Main" }
      );
      return;
    }

    setFiles((prev) => [...prev, newFile]);
    if (onFileSelected) onFileSelected(newFile);
  };

  // ---------- drop handling ----------
  const onDrop = useCallback(
      (acceptedFiles) => {
        const uniqueFiles = acceptedFiles.filter(
            (file) => !files.find((existingFile) => existingFile.name === file.name)
        );

        if (files.length + uniqueFiles.length > maxFiles) {
          toast.error(`Cannot add more than ${maxFiles} files.`, {
            autoClose: 5000,
            containerId: isOnTheModal ? "Modal" : "Main",
          });
          return;
        }

        if (uniqueFiles[0]) addFiles(uniqueFiles[0]);
      },
      [files, maxFiles, addFiles, isOnTheModal]
  );

  const removeFile = (fileName) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  };

  // ---------- fake progress ----------
  useEffect(() => {
    if (files.length === 0) return;

    const totalFiles = files.length;
    let uploadedFiles = 0;

    const uploadInterval = setInterval(() => {
      if (uploadedFiles < totalFiles) {
        const progress = Math.round(((uploadedFiles + 1) / totalFiles) * 100);
        setUploadProgress(progress);
        uploadedFiles++;
      } else {
        clearInterval(uploadInterval);
        setUploadProgress(0);
      }
    }, 500);

    return () => clearInterval(uploadInterval);
  }, [files]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles,
    type: "file",
    disabled: disabled || documentData.docCategory === null,
    accept,
  });

  // ---------- UI ----------
  const dropzoneStyle = {
    border: `0.5px solid ${errors.file ? "#e0b4b4" : "#d3d3d3"}`,
    backgroundColor: `${errors.file ? "#fff6f6" : "#fff"}`,
    borderRadius: "4px",
    padding: "20px",
    textAlign: "center",
    cursor: "pointer",
    maxWidth: "100%",
  };

  const textStyle = { color: `${errors.file ? "#9f3a38" : "#d3d3d3"}` };
  const previewContainerStyle = { overflowX: "auto" };
  const filePreviewsStyle = { display: "flex" };

  const filePreview = (file) => {
    const fileType = file.type || "";
    const isImage = fileType.includes("image");

    if (isImage) {
      return (
          <img
              src={URL.createObjectURL(file)}
              alt="preview"
              style={{ maxWidth: "50px", maxHeight: "50px" }}
          />
      );
    }

    let icon = "file outline";
    if (fileType.includes("pdf")) icon = "file pdf outline";
    else if (fileType.includes("word")) icon = "file word outline";
    else if (fileType.includes("excel")) icon = "file excel outline";

    return <Icon name={icon} size="large" />;
  };

  return (
      <div>
        <div {...getRootProps()} style={dropzoneStyle}>
          <input {...getInputProps()} />
          <p style={textStyle}>
            Drag 'n' drop some files here, or click to select files
          </p>
          {files.length > 0 && (
              <div style={previewContainerStyle}>
                <div style={filePreviewsStyle}>
                  {files.map((file) => (
                      <div
                          key={file.name}
                          style={{
                            border: "1px solid #d3d3d3",
                            borderRadius: "4px",
                            padding: "5px",
                            margin: "0 2px",
                            display: "flex",
                            justifyContent: "space-between",
                            position: "relative",
                          }}
                      >
                        <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "5px 10px",
                            }}
                        >
                          {filePreview(file)}
                          <span style={{ marginLeft: "10px" }}>{file.name}</span>
                          <Icon
                              name="close"
                              color="red"
                              style={{
                                cursor: "pointer",
                                position: "absolute",
                                right: "0",
                                top: "0",
                                zIndex: "1",
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                removeFile(file.name);
                              }}
                          />
                        </div>
                      </div>
                  ))}
                </div>
              </div>
          )}
        </div>

        {uploadProgress > 0 && (
            <Progress percent={uploadProgress} progress success />
        )}
      </div>
  );
};

export default FileUploader;
